// frontend/src/Components/SyncTo/RecordingProvider.jsx
// Phase 30 — React context placed above the HashRouter in App.js.
// (Invariant #6 / PITFALLS #21.)
//
// OWNS:
//   - useSyncToRecorder() state (delegated)
//   - Modal open/close (D-01 / D-03)
//   - Orphan prompt state (D-08) — userId-scoped, 24h TTL (via scanOrphans)
//   - SYNC-04 sub-dialogs (mic-denied, first-silence, rolling-warn, zero-byte)
//   - beforeunload handler while state !== 'idle' (PITFALLS #10)
//   - D-05 auto-stop toast (via NotificationContext — WHY this provider nests
//     INSIDE NotificationContext.Provider in App.js, not outside)
//
// PLACEMENT: App.js — INSIDE NotificationContext.Provider,
// OUTSIDE the HashRouter element.
//
// Resume semantics on orphan = DOWNLOAD (Phase 30 default).
// MediaRecorder state is gone after tab close; true "continue recording"
// is a Phase 34 extension.
//
// This file deliberately imports nothing from react-router-dom and renders
// no router elements — it is router-agnostic by design.

import React, {
  createContext, useContext, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { useNotification } from '../../NotificationContext';
import { useSyncToRecorder } from './hooks/useSyncToRecorder';
import { scanOrphans, assembleBlob, purgeBySessionKey } from './hooks/useSyncToIDB';
import { readUserIdFromJwt } from './helpers/userIdFromJwt';
import { downloadBlob, buildDownloadFilename } from './helpers/downloadBlob';
import ReviewModal from './ReviewModal';
import FailureModal from './FailureModal';
import { useSyncToApi } from './hooks/useSyncToApi';

export const RecordingContext = createContext(null);
export function useRecording() { return useContext(RecordingContext); }

// Stage 4 follow-up — separate "actions only" context so consumers that just
// need to call openModal / openJobForReview / pollPendingForPatient (e.g.
// BootNav, Notification, Patientsappointment, Session, SyncToButton) don't
// re-render on every audio level / timer tick during recording. Only RecordModal
// genuinely needs the live state values, so it keeps using useRecording().
export const RecordingActionsContext = createContext(null);
export function useRecordingActions() { return useContext(RecordingActionsContext); }

const ORPHAN_DISMISS_KEY = 'syncto.orphans.dismissed';

// Returns true when the current hash route is on the matching patient's page.
// Used to gate ReviewModal auto-open: if the user is elsewhere (dashboard,
// bookings list, another patient), we surface a notification only — they
// click through to land on the patient and then see the modal.
function isOnPatientPage(patientId) {
  if (!patientId) return false;
  try {
    const hash = (window.location.hash || '').toLowerCase();
    return hash.includes(`/patients/appointment/${String(patientId).toLowerCase()}`);
  } catch (_e) {
    return false;
  }
}

export default function RecordingProvider({ children }) {
  const notification = useNotification();
  const addNotification = notification?.addNotification;
  const upsertNotification = notification?.upsertNotification;

  const [modalOpen, setModalOpen] = useState(false);
  const [meta, setMeta] = useState(null);

  // Phase 32 — Review-then-apply state.
  const [reviewOpen,       setReviewOpen]       = useState(false);
  const [reviewCleaned,    setReviewCleaned]    = useState('');
  const [reviewFailed,     setReviewFailed]     = useState(false);
  const [reviewSessionId,  setReviewSessionId]  = useState(null);
  const [reviewTranscript, setReviewTranscript] = useState('');
  const [reviewDurationMs, setReviewDurationMs] = useState(0);
  const [transcribing,     setTranscribing]     = useState(false);
  // Phase 33 — Template-variant review state. Threaded from meta (Session.jsx
  // passes templatePages + existingCapturedValues to openModal) on openModal,
  // then populated with routedValues + dropped from the /transcribe Template-
  // branch response inside onStopped.
  const [reviewRoutedValues,     setReviewRoutedValues]     = useState({});
  const [reviewDropped,          setReviewDropped]          = useState([]);
  const [reviewExistingCaptured, setReviewExistingCaptured] = useState({});
  const [reviewTemplateId,       setReviewTemplateId]       = useState(null);
  const [reviewTemplatePages,    setReviewTemplatePages]    = useState([]);
  const [reviewRoutingFailed,    setReviewRoutingFailed]    = useState(false);
  // True when the AI ran successfully but produced zero routable values
  // (e.g. transcript was too short / off-topic / dictated content didn't
  // match any template field). Distinct from reviewRoutingFailed (which
  // means the AI call itself threw). Both drive the same empty-state banner
  // in ReviewModal but tell the user a different story.
  const [reviewRoutingEmpty,     setReviewRoutingEmpty]     = useState(false);
  // Tracks the IDB sessionKey for the in-flight review so we can purge chunks
  // after apply-success or review-close. useSyncToRecorder.stop() no longer
  // purges when onStopped is set — the caller (us) owns the chunks so
  // FailureModal can re-assemble for download.
  const [reviewSessionKey,       setReviewSessionKey]       = useState(null);

  // Phase 34 — Failure fallback state. Populated when transcribe() returns
  // error.attempts >= 3 (exhausted) OR status === 504 (timeout → timeoutMode).
  const [failureOpen,        setFailureOpen]        = useState(false);
  const [failureAttempts,    setFailureAttempts]    = useState(0);
  const [failureMessage,     setFailureMessage]     = useState('');
  const [failureSessionKey,  setFailureSessionKey]  = useState(null);
  const [failureMimeType,    setFailureMimeType]    = useState('');
  const [failureTimeoutMode, setFailureTimeoutMode] = useState(false);
  // Ref synced to recorder.sessionKey below (recorder declared AFTER
  // onStopped — ref lets onStopped read the current sessionKey at call-time
  // without re-ordering the declarations).
  const sessionKeyRef = useRef(null);

  const { transcribe, transcribeAsync, getJob, listJobs, dismissJob } = useSyncToApi();
  // Track jobIds for which we've already opened ReviewModal — prevents the
  // socket event + the on-mount listJobs call from racing into two opens.
  const seenJobIdsRef = useRef(new Set());
  // Stage 4 follow-up — poll fallback when socket events aren't delivered
  // (Web PubSub config drift, mid-session reconnect, dev environment quirks).
  // Active jobIds are polled every 6s until COMPLETE/FAILED. The socket
  // path remains the primary trigger; this is belt-and-braces.
  const activePollersRef = useRef(new Map()); // jobId -> intervalId
  // Forward-reference: onStopped (declared first) needs to call openJobForReview
  // (declared later) when the poll fallback fires. A ref breaks the temporal
  // dead zone without reordering the rest of the file.
  const openJobForReviewRef = useRef(null);

  const [micDeniedOpen,       setMicDeniedOpen]       = useState(false);
  const [firstSilenceOpen,    setFirstSilenceOpen]    = useState(false);
  const [rollingSilenceOpen,  setRollingSilenceOpen]  = useState(false);
  const [zeroByteFailureOpen, setZeroByteFailureOpen] = useState(false);

  const [orphans, setOrphans] = useState([]);
  const [orphanDismissed, setOrphanDismissed] = useState(false);

  // Stage 3 — async path. The recording stops, audio is uploaded to Blob
  // Storage, a noteTranscriptionJobs row is queued, and we return control to
  // the user immediately. The transcript arrives later via Socket.IO
  // ('syncto:job-complete' → openJobForReview) or via patient-page recovery
  // (listJobs on mount). The previous synchronous behaviour is preserved as
  // a fallback inside transcribe() when needed (e.g. retry from FailureModal).
  const onStopped = useCallback(async (blob) => {
    setModalOpen(false);
    if (!blob || blob.size === 0) return;
    const capturedSessionKey = sessionKeyRef.current;
    const mode = (meta && meta.mode) || 'note';
    const sessionId = meta && meta.sessionId;
    const templateId = meta && meta.templateId;
    const locale = meta && meta.locale;
    const mimeType = blob.type;

    // Stash the sessionKey on the meta object so when ReviewModal later opens
    // (after the worker completes) we can purge IDB chunks on Apply-success.
    setReviewSessionKey(capturedSessionKey);

    // Surface a brief "uploading" overlay during the multipart upload itself
    // (typically a few seconds). Once upload completes the worker takes over
    // and we close this overlay — user is free to navigate away.
    setTranscribing(true);
    const { data, error: err } = await transcribeAsync({
      blob, mode, mimeType, sessionId, templateId, locale,
    });
    setTranscribing(false);

    if (err || !data || !data.jobId) {
      // Upload itself failed (network / 4xx / 503 audio-container-not-set).
      // Surface the existing FailureModal so the user can download / retry.
      // IDB chunks are KEPT here — they're the only copy of the audio.
      setFailureAttempts(1);
      setFailureMessage((err && err.message) || 'Audio upload failed');
      setFailureSessionKey(capturedSessionKey);
      setFailureMimeType(mimeType || (blob && blob.type) || '');
      setFailureTimeoutMode(false);
      setFailureOpen(true);
      return;
    }

    // 202 success — audio is durably in Azure Blob Storage and the worker
    // will process it. The IDB chunks are now redundant; purge so the orphan
    // scan (which prompts "Recording found — download / discard") doesn't
    // misfire on the next page load. We're keeping `reviewSessionKey` set
    // for the apply-success purge path purely as a no-op safety net.
    if (capturedSessionKey) {
      purgeBySessionKey(capturedSessionKey).catch(() => {});
    }

    // Statement-worker pattern — push ONE notification keyed by jobId that we
    // will UPDATE in place when the job completes (or fails). This replaces
    // the previous "queued" + "complete" pair with a single progressing row.
    const pollJobId = data.jobId;
    const patientIdForJob = (meta && meta.patientId) || null;
    if (typeof upsertNotification === 'function') {
      upsertNotification({
        id: `syncto-job-${pollJobId}`,
        dedupeKey: `syncto-job-${pollJobId}`,
        type: 'syncto',
        message: 'Transcription is processing in the background…',
        timestamp: Date.now(),
        isRead: false,
        jobId: pollJobId,
        sessionId,
        patientId: patientIdForJob,
        mode,
        meta: { jobId: pollJobId, sessionId, patientId: patientIdForJob, mode, status: 'PROCESSING' },
      });
    }

    // Poll fallback. The socket path is primary, but if the Web PubSub frame
    // doesn't arrive (env config / network / hub mismatch) the user would
    // never see the modal. This poller closes the gap by hitting GET
    // /transcription-jobs/:jobId every 6s until status transitions to
    // COMPLETE / FAILED / APPLIED / DISMISSED. On COMPLETE we update the bell
    // notification AND open the ReviewModal IF the user is currently on this
    // patient's page (otherwise the bell update is enough — they click the
    // notification to navigate + open).
    if (!activePollersRef.current.has(pollJobId)) {
      const intervalId = setInterval(async () => {
        const { data: job } = await getJob(pollJobId);
        if (!job) return;
        if (job.status === 'COMPLETE') {
          clearInterval(intervalId);
          activePollersRef.current.delete(pollJobId);
          if (typeof upsertNotification === 'function') {
            upsertNotification({
              id: `syncto-job-${pollJobId}`,
              type: 'syncto',
              message: 'Your transcription is ready for review',
              timestamp: Date.now(),
              isRead: false,
              jobId: pollJobId,
              sessionId: job.sessionId,
              patientId: job.patientId,
              mode: job.mode,
              meta: { jobId: pollJobId, sessionId: job.sessionId, patientId: job.patientId, mode: job.mode, status: 'COMPLETE' },
            });
          }
          if (isOnPatientPage(job.patientId) && typeof openJobForReviewRef.current === 'function') {
            openJobForReviewRef.current(pollJobId);
          }
        } else if (job.status === 'FAILED') {
          clearInterval(intervalId);
          activePollersRef.current.delete(pollJobId);
          if (typeof upsertNotification === 'function') {
            upsertNotification({
              id: `syncto-job-${pollJobId}`,
              type: 'syncto',
              message: 'Transcription failed — please try again',
              timestamp: Date.now(),
              isRead: false,
              jobId: pollJobId,
              meta: { jobId: pollJobId, status: 'FAILED' },
            });
          }
        } else if (job.status === 'APPLIED' || job.status === 'DISMISSED') {
          clearInterval(intervalId);
          activePollersRef.current.delete(pollJobId);
        }
      }, 6000);
      activePollersRef.current.set(pollJobId, intervalId);
    }
  }, [meta, transcribeAsync, upsertNotification, getJob]);

  const recorder = useSyncToRecorder({
    onMicDenied:          useCallback(() => setMicDeniedOpen(true), []),
    onZeroByteFailure:    useCallback(() => setZeroByteFailureOpen(true), []),
    onFirstSilence:       useCallback(() => setFirstSilenceOpen(true), []),
    onRollingSilenceWarn: useCallback(() => setRollingSilenceOpen(true), []),
    onAutoStop: useCallback(() => {
      addNotification?.({
        type: 'info',
        message: 'Auto-stopped after 60 seconds of silence — your recording was downloaded.',
      });
    }, [addNotification]),
    onStopped,
    onCancelled: useCallback(() => { setModalOpen(false); }, []),
    downloadOnStop: false,
  });

  // Phase 34 — keep the ref in sync with the live recorder.sessionKey so the
  // onStopped useCallback (declared BEFORE useSyncToRecorder above) can read
  // the current sessionKey at call-time when pushing into failure state.
  useEffect(() => {
    sessionKeyRef.current = recorder?.sessionKey || null;
  }, [recorder?.sessionKey]);

  const openModal = useCallback((m) => {
    setMeta(m || null);
    setModalOpen(true);
  }, []);
  const closeModal = useCallback(() => { setModalOpen(false); }, []);
  // Allow children (RecordModal's combined mode picker) to merge mode /
  // templateId / templatePages into meta after the user makes a choice in
  // the modal — onStopped reads meta to build the transcribe payload, so
  // these fields must end up in context state, not just local component state.
  const updateMeta = useCallback((patch) => {
    setMeta((prev) => ({ ...(prev || {}), ...(patch || {}) }));
  }, []);

  // Populate review state from a completed job and open ReviewModal. Called
  // by:
  //   1. The 'syncto:job-complete' Socket.IO listener in BootNav (immediate
  //      surfacing when the user is online).
  //   2. The patient-page recovery flow — Session.jsx / patient mount calls
  //      pollPendingForPatient(patientId) which loops back into this when a
  //      COMPLETE job is found.
  // Idempotent via seenJobIdsRef — re-firing for the same jobId is a no-op so
  // the patient-mount listJobs call doesn't re-open a modal the user is
  // already reviewing or has just dismissed.
  const openJobForReview = useCallback(async (jobId) => {
    console.log('[syncto] openJobForReview called', jobId); // TEMP DIAG
    if (!jobId) return;
    const id = String(jobId);
    if (seenJobIdsRef.current.has(id)) {
      console.log('[syncto] openJobForReview skipped — already seen', id); // TEMP DIAG
      return;
    }
    const { data: job, error: err } = await getJob(id);
    if (err || !job) {
      console.warn('[syncto] getJob failed', { id, err, job }); // TEMP DIAG
      return;
    }
    console.log('[syncto] getJob result', { id, status: job.status, mode: job.mode }); // TEMP DIAG
    if (job.status !== 'COMPLETE') return; // wrong-status guard

    seenJobIdsRef.current.add(id);

    // Mirror the previous /transcribe response shape onto the review state
    // so ReviewModal renders identically whether the data arrived sync or
    // via the worker.
    setReviewCleaned(job.cleaned || '');
    setReviewFailed(!!job.cleanupFailed);
    setReviewSessionId(job.sessionId || null);
    setReviewTranscript(typeof job.transcript === 'string' ? job.transcript : '');
    setReviewDurationMs(typeof job.durationMs === 'number' ? job.durationMs : 0);

    const isTemplateMode = job.mode === 'template';
    if (isTemplateMode) {
      const jobRoutedValues = job.routedValues || {};
      setReviewRoutedValues(jobRoutedValues);
      setReviewDropped(Array.isArray(job.dropped) ? job.dropped : []);
      setReviewTemplateId(job.templateId || null);
      // Surface the worker's routing outcome so the modal can render an
      // explicit empty-state banner rather than a confusing blank form.
      // `routingEmpty` falls back to "routedValues is empty" so the banner
      // shows even for older jobs persisted before the flag was added.
      setReviewRoutingFailed(!!job.routingFailed);
      setReviewRoutingEmpty(
        !job.routingFailed &&
        (job.routingEmpty === true || Object.keys(jobRoutedValues).length === 0),
      );
      // templatePages + existingCapturedValues need to come from the patient
      // context — they aren't on the job document. Caller (patient page) can
      // pre-set meta with these before the job opens, or we leave them empty
      // and the ReviewModal Template variant degrades to "no preview".
      if (meta && Array.isArray(meta.templatePages)) {
        setReviewTemplatePages(meta.templatePages);
      }
      if (meta && meta.existingCapturedValues) {
        setReviewExistingCaptured(meta.existingCapturedValues);
      }
    } else {
      setReviewRoutedValues({});
      setReviewDropped([]);
      setReviewTemplateId(null);
      setReviewTemplatePages([]);
      setReviewExistingCaptured({});
      setReviewRoutingFailed(false);
      setReviewRoutingEmpty(false);
    }
    // Stash jobId + sessionId + patientId on meta. sessionId/patientId are
    // critical for the 'syncto:applied' window event filter in Session.jsx —
    // without them, the event handler can't tell which Session instance
    // should refetch when the modal was opened via socket/poll (no openModal
    // call beforehand) and existingSessionId is null after a fresh remount.
    setMeta((prev) => ({
      ...(prev || {}),
      jobId: id,
      sessionId: job.sessionId || prev?.sessionId || null,
      patientId: job.patientId || prev?.patientId || null,
      mode: job.mode || prev?.mode || 'note',
    }));
    setReviewOpen(true);
  }, [getJob, meta]);

  // Sync the ref so onStopped's poll fallback can call openJobForReview
  // without a TDZ issue (onStopped is declared before openJobForReview).
  useEffect(() => { openJobForReviewRef.current = openJobForReview; }, [openJobForReview]);

  // Clean up any active pollers on unmount so we don't leak intervals.
  useEffect(() => {
    const pollers = activePollersRef.current;
    return () => {
      for (const intervalId of pollers.values()) {
        clearInterval(intervalId);
      }
      pollers.clear();
    };
  }, []);

  // Patient-page recovery: list active jobs for the patient. Previously this
  // auto-opened ReviewModal for any COMPLETE job — but that hijacked the
  // user's flow when they were trying to start a new recording. Now it only
  // returns the count; the user opens the modal explicitly by clicking the
  // bell notification (whose click handler calls openJobForReview).
  const pollPendingForPatient = useCallback(async (patientId) => {
    if (!patientId) return { pending: 0 };
    const { data, error: err } = await listJobs({ patientId });
    if (err || !data) return { pending: 0 };
    const jobs = Array.isArray(data.jobs) ? data.jobs : [];
    return { pending: jobs.length };
  }, [listJobs]);

  const currentUserIdRef = useRef(null);
  useEffect(() => {
    let cancelled = false;

    async function runScan(userId) {
      if (cancelled) return;
      currentUserIdRef.current = userId;
      const dismissed = sessionStorage.getItem(ORPHAN_DISMISS_KEY);
      if (dismissed === userId) { setOrphanDismissed(true); return; }
      try {
        const rows = await scanOrphans(userId);
        if (cancelled) return;
        const filtered = (rows || []).filter((row) => row.userId === userId);
        setOrphans(filtered);
        setOrphanDismissed(false);
      } catch {
        /* IDB open failure — silently skip */
      }
    }

    const initialUid = readUserIdFromJwt();
    if (initialUid) {
      runScan(initialUid);
      return () => { cancelled = true; };
    }

    const interval = setInterval(() => {
      if (cancelled) { clearInterval(interval); return; }
      const uid = readUserIdFromJwt();
      if (uid) {
        clearInterval(interval);
        runScan(uid);
      }
    }, 1000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const dismissOrphanPrompt = useCallback(() => {
    setOrphanDismissed(true);
    const uid = currentUserIdRef.current;
    if (uid) { try { sessionStorage.setItem(ORPHAN_DISMISS_KEY, uid); } catch {} }
  }, []);

  const downloadOrphan = useCallback(async (sessionKey) => {
    const row = orphans.find((o) => o.sessionKey === sessionKey);
    if (!row) return;
    try {
      const blob = await assembleBlob(sessionKey, row.mimeType);
      if (blob && blob.size > 0) {
        downloadBlob(blob, buildDownloadFilename(sessionKey, row.mimeType));
      }
    } finally {
      await purgeBySessionKey(sessionKey);
      setOrphans((prev) => prev.filter((o) => o.sessionKey !== sessionKey));
    }
  }, [orphans]);

  const discardOrphan = useCallback(async (sessionKey) => {
    try { await purgeBySessionKey(sessionKey); } catch {}
    setOrphans((prev) => prev.filter((o) => o.sessionKey !== sessionKey));
  }, []);

  // Phase 30 default: Resume aliases to Download. MediaRecorder state is gone
  // after tab close; true "continue recording" is a Phase 34 extension.
  const resumeOrphan = useCallback(async (sessionKey) => {
    await downloadOrphan(sessionKey);
  }, [downloadOrphan]);

  useEffect(() => {
    if (recorder.state === 'idle') return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [recorder.state]);

  const dismissMicDenied       = useCallback(() => setMicDeniedOpen(false),       []);
  const dismissFirstSilence    = useCallback(() => setFirstSilenceOpen(false),    []);
  const dismissRollingSilence  = useCallback(() => setRollingSilenceOpen(false),  []);
  const dismissZeroByteFailure = useCallback(() => setZeroByteFailureOpen(false), []);

  // Phase 34 — FailureModal handlers.
  const dismissFailure = useCallback(() => {
    setFailureOpen(false);
    setFailureAttempts(0);
    setFailureMessage('');
    setFailureSessionKey(null);
    setFailureMimeType('');
    setFailureTimeoutMode(false);
  }, []);

  // Phase 34 SC7 — re-fetch /api/sync-to/user-feature-flag when ReviewModal
  // surfaces a 403 mid-Apply. The SyncToButton render gate reads the JWT
  // (sessionStorage 'user' → decoded.features.syncTo); we cannot refresh the
  // JWT without a full re-login, so we PERSIST a fresh { enabled, checkedAt }
  // signal to a new sessionStorage key ('syncto.flag.fresh') as an
  // opportunistic freshness hint. The backend is the authority — this key
  // cannot grant access; it only disables UX surfaces earlier. IDB chunks
  // are NOT purged here (PITFALLS #11 — user can still download via
  // FailureModal on a subsequent session if orphan-scan surfaces them).
  const refreshFeatureFlag = useCallback(async () => {
    try {
      const URL_BASE =
        process.env.REACT_APP_API_BASE_PROD_URL ||
        process.env.REACT_APP_API_BASE_URL ||
        'http://localhost:3002';
      const token = sessionStorage.getItem('user');
      const resp = await fetch(URL_BASE + '/api/sync-to/user-feature-flag', {
        headers: token ? { Authorization: 'Bearer ' + token } : {},
      });
      if (!resp.ok) return;
      const json = await resp.json();
      try {
        sessionStorage.setItem('syncto.flag.fresh', JSON.stringify({
          enabled: !!(json && json.enabled),
          checkedAt: Date.now(),
        }));
      } catch { /* storage quota — ignore */ }
    } catch { /* network — ignore, fail-closed on next re-check */ }
  }, []);

  const retryFailure = useCallback(async (blob) => {
    if (!blob) return;
    // Snapshot sessionKey pre-await — see onStopped for the rationale.
    const capturedSessionKey = sessionKeyRef.current;
    setFailureOpen(false);
    setTranscribing(true);
    const mode = (meta && meta.mode) || 'note';
    const sessionId = meta && meta.sessionId;
    const templateId = meta && meta.templateId;
    const locale = meta && meta.locale;
    const mimeType = blob.type;
    const { data, error: err } = await transcribe({ blob, mode, mimeType, templateId, locale });
    setTranscribing(false);
    if (err || !data) {
      setFailureAttempts(typeof err?.attempts === 'number' ? err.attempts : 3);
      setFailureMessage((err && err.message) || 'Unknown error');
      setFailureSessionKey(capturedSessionKey);
      setFailureMimeType(mimeType || '');
      setFailureTimeoutMode(err?.status === 504);
      setFailureOpen(true);
      return;
    }
    // Success on retry — populate review state exactly like the happy-path onStopped.
    setReviewCleaned(data.cleaned || '');
    setReviewFailed(!!data.cleanupFailed);
    setReviewSessionId(sessionId || null);
    setReviewSessionKey(capturedSessionKey);
    setReviewTranscript(typeof data.transcript === 'string' ? data.transcript : '');
    setReviewDurationMs(typeof data.durationMs === 'number' ? data.durationMs : 0);
    const isTemplateMode = data.mode === 'template' && meta?.mode === 'template';
    if (isTemplateMode) {
      const syncRoutedValues = data.routedValues || {};
      setReviewRoutedValues(syncRoutedValues);
      setReviewDropped(Array.isArray(data.dropped) ? data.dropped : []);
      setReviewTemplateId((meta && meta.templateId) || null);
      setReviewTemplatePages((meta && Array.isArray(meta.templatePages)) ? meta.templatePages : []);
      setReviewExistingCaptured((meta && meta.existingCapturedValues) || {});
      // Match the worker path — surface the routing outcome to ReviewModal.
      setReviewRoutingFailed(!!data.routingFailed);
      setReviewRoutingEmpty(!data.routingFailed && Object.keys(syncRoutedValues).length === 0);
    } else {
      setReviewRoutedValues({});
      setReviewDropped([]);
      setReviewTemplateId(null);
      setReviewTemplatePages([]);
      setReviewExistingCaptured({});
      setReviewRoutingFailed(false);
      setReviewRoutingEmpty(false);
    }
    setReviewOpen(true);
  }, [meta, transcribe]);

  const value = useMemo(() => ({
    state: recorder.state,
    timer: recorder.timer,
    level: recorder.level,
    sessionKey: recorder.sessionKey,
    start:  recorder.start,
    pause:  recorder.pause,
    resume: recorder.resume,
    stop:   recorder.stop,
    cancel: recorder.cancel,
    modalOpen, meta, openModal, closeModal, updateMeta,
    openJobForReview, pollPendingForPatient,
    micDeniedOpen,       dismissMicDenied,
    firstSilenceOpen,    dismissFirstSilence,
    rollingSilenceOpen,  dismissRollingSilence,
    zeroByteFailureOpen, dismissZeroByteFailure,
    orphans: orphanDismissed ? [] : orphans,
    dismissOrphanPrompt, downloadOrphan, discardOrphan, resumeOrphan,
  }), [
    recorder.state, recorder.timer, recorder.level, recorder.sessionKey,
    recorder.start, recorder.pause, recorder.resume, recorder.stop, recorder.cancel,
    modalOpen, meta, openModal, closeModal, updateMeta,
    openJobForReview, pollPendingForPatient,
    micDeniedOpen, dismissMicDenied,
    firstSilenceOpen, dismissFirstSilence,
    rollingSilenceOpen, dismissRollingSilence,
    zeroByteFailureOpen, dismissZeroByteFailure,
    orphans, orphanDismissed, dismissOrphanPrompt, downloadOrphan, discardOrphan, resumeOrphan,
  ]);

  // Actions-only memo — deliberately excludes recorder.state / timer / level /
  // sessionKey so subscribers using useRecordingActions don't re-render on every
  // audio level update. Functions inside are useCallback'd so their refs are
  // stable while not recording, and the meta object only changes on legitimate
  // events (modal open, job complete) — never per audio frame.
  const actionsValue = useMemo(() => ({
    start:  recorder.start,
    pause:  recorder.pause,
    resume: recorder.resume,
    stop:   recorder.stop,
    cancel: recorder.cancel,
    openModal, closeModal,
    openJobForReview, pollPendingForPatient,
    dismissMicDenied,
    dismissFirstSilence,
    dismissRollingSilence,
    dismissZeroByteFailure,
    dismissOrphanPrompt, downloadOrphan, discardOrphan, resumeOrphan,
  }), [
    recorder.start, recorder.pause, recorder.resume, recorder.stop, recorder.cancel,
    openModal, closeModal,
    openJobForReview, pollPendingForPatient,
    dismissMicDenied,
    dismissFirstSilence,
    dismissRollingSilence,
    dismissZeroByteFailure,
    dismissOrphanPrompt, downloadOrphan, discardOrphan, resumeOrphan,
  ]);

  return (
    <RecordingContext.Provider value={value}>
      <RecordingActionsContext.Provider value={actionsValue}>
      {children}
      <ReviewModal
        show={reviewOpen}
        onClose={() => {
          setReviewOpen(false);
          // Stage 4 — flag the underlying job DISMISSED so patient-page
          // recovery doesn't re-surface the same review on next mount.
          // Fire-and-forget; the user has explicitly dismissed.
          const dismissedJobId = meta && meta.jobId;
          if (dismissedJobId) {
            dismissJob(dismissedJobId).catch(() => {});
          }
          // Chunks are no longer needed — user dismissed after reviewing.
          // (useSyncToRecorder.stop() no longer purges when onStopped is set.)
          if (reviewSessionKey) {
            purgeBySessionKey(reviewSessionKey).catch(() => {});
            setReviewSessionKey(null);
          }
          setMeta(null);
        }}
        onApplied={(result) => {
          // Phase 34 SC7 — 403 feature-flag-disabled path. Keep ReviewModal
          // OPEN so the user sees the disabled banner; do NOT forward the
          // result to meta.onApplied (Session.jsx should not switch tabs on a
          // failed apply). Re-fetch /user-feature-flag to persist a fresh
          // signal and leave IDB chunks intact (PITFALLS #11).
          if (result && result.featureDisabled) {
            refreshFeatureFlag();
            return;
          }
          setReviewOpen(false);
          // Successful apply — audio has served its purpose; purge IDB chunks.
          if (reviewSessionKey) {
            purgeBySessionKey(reviewSessionKey).catch(() => {});
            setReviewSessionKey(null);
          }
          // Stage 4 follow-up — broadcast a window event so any open Session
          // view can refresh itself, even when the original meta.onApplied
          // closure is stale (modal opened via socket event after a long
          // worker delay, or via patient-page recovery on a fresh page load
          // where openModal was never called).
          try {
            window.dispatchEvent(new CustomEvent('syncto:applied', {
              detail: {
                sessionId: (meta && meta.sessionId) || (result && result.sessionId) || null,
                patientId: (meta && meta.patientId) || null,
                mode:      result && result.mode,
                applied:   result && result.applied,
                jobId:     meta && meta.jobId,
              },
            }));
          } catch (_e) { /* ignore — non-critical telemetry path */ }
          if (meta && typeof meta.onApplied === 'function') {
            meta.onApplied(result);
          }
          // Clear meta after apply so its stale closures (referencing the
          // unmounted Session.jsx instance) don't carry into the next flow.
          // The next openModal call will re-populate meta cleanly.
          setMeta(null);
        }}
        // Phase 33 — mode is driven by meta (Session.jsx sets 'template' when
        // ModeModal is wired in Phase 34; currently defaults to 'note'). The
        // ReviewModal branches its render on this value.
        mode={(meta && meta.mode) || 'note'}
        sessionId={reviewSessionId}
        jobId={(meta && meta.jobId) || null}
        cleaned={reviewCleaned}
        cleanupFailed={reviewFailed}
        transcript={reviewTranscript}
        durationMs={reviewDurationMs}
        // Phase 33 — Template-variant props (undefined / empty for note mode).
        templateId={reviewTemplateId}
        templatePages={reviewTemplatePages}
        routedValues={reviewRoutedValues}
        dropped={reviewDropped}
        existingCapturedValues={reviewExistingCaptured}
        routingFailed={reviewRoutingFailed}
        routingEmpty={reviewRoutingEmpty}
      />
      <Modal
        show={transcribing}
        backdrop="static"
        keyboard={false}
        centered
        size="sm"
      >
        <Modal.Body className="text-center py-4">
          <Spinner animation="border" variant="primary" role="status" className="mb-3" />
          <div className="small text-muted">
            Uploading audio — you can navigate away once this finishes.
          </div>
        </Modal.Body>
      </Modal>
      <FailureModal
        show={failureOpen}
        onClose={dismissFailure}
        sessionKey={failureSessionKey}
        mimeType={failureMimeType}
        attempts={failureAttempts}
        errorMessage={failureMessage}
        timeoutMode={failureTimeoutMode}
        onRetry={retryFailure}
        onDismiss={dismissFailure}
      />
      </RecordingActionsContext.Provider>
    </RecordingContext.Provider>
  );
}
